// src/components/filters/MetricPresets.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  InputBase,
  IconButton,
  Popper,
  Typography,
  ClickAwayListener,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme, alpha } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { trackEvent } from '../../utils/analytics';
import {
  saveMetricPreset,
  deleteMetricPreset,
} from "../../features/favorites/favoritesSlice";

function MetricPresets({ selectedMetrics, setSelectedMetrics, allColDefs, isAuthenticated: isAuthenticatedProp, leftBorderOnly = false }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [newPresetName, setNewPresetName] = useState("");
  const [activePreset, setActivePreset] = useState(null);
  const [showSignupDialog, setShowSignupDialog] = useState(false);

  const theme = useTheme();
  const inputRef = useRef(null);
  const newPresetInputRef = useRef(null);

  // Get metric presets from Redux
  const metricPresets = useSelector((state) => state.favorites.metricPresets || {});
  const dispatch = useDispatch();
  const { getAccessTokenSilently, isAuthenticated: authIsAuthenticated, loginWithRedirect } = useAuth0();

  // Use prop if provided (for dev mode override), otherwise use Auth0
  const isAuthenticated = isAuthenticatedProp !== undefined ? isAuthenticatedProp : authIsAuthenticated;

  // Handle opening/closing the menu
  const handleToggle = (event) => {
    if (!isAuthenticated) {
      setShowSignupDialog(true);
      return;
    }
    const isClosing = Boolean(anchorEl);
    setAnchorEl(isClosing ? null : event.currentTarget);
  };
  const open = Boolean(anchorEl);

  // Autofocus new preset input when the menu opens
  useEffect(() => {
    if (anchorEl && newPresetInputRef.current) {
      setTimeout(() => newPresetInputRef.current?.focus(), 100);
    }
  }, [anchorEl]);

  // Helper: compare two arrays of metrics
  const isPresetChanged = (name) => {
    const original = metricPresets[name] || [];
    const current = selectedMetrics;
    if (original.length !== current.length) return true;
    const sortedOriginal = [...original].sort();
    const sortedCurrent = [...current].sort();
    return !sortedOriginal.every((val, idx) => val === sortedCurrent[idx]);
  };

  // Save a new preset
  const saveNewPreset = async () => {
    const name = newPresetName.trim();
    if (!name || selectedMetrics.length === 0) return;

    try {
      const token = await getAccessTokenSilently({ audience: "https://api.softwareiq.io" });
      await dispatch(
        saveMetricPreset({ token, presetName: name, metrics: selectedMetrics })
      ).unwrap();
      trackEvent('preset_change', { preset_type: 'metric', operation: 'create', result_count: selectedMetrics.length });
      setNewPresetName("");
      setActivePreset(name);
    } catch (err) {
      console.error("Error saving metric preset:", err);
    }
  };

  // Save changes to an existing preset
  const saveExistingPreset = async (name) => {
    if (!name) return;
    try {
      const token = await getAccessTokenSilently({ audience: "https://api.softwareiq.io" });
      await dispatch(
        saveMetricPreset({ token, presetName: name, metrics: selectedMetrics })
      ).unwrap();
      trackEvent('preset_change', { preset_type: 'metric', operation: 'update' });
    } catch (err) {
      console.error("Error updating metric preset:", err);
    }
  };

  // Load a preset into selectedMetrics
  const loadPreset = (name) => {
    const metrics = metricPresets[name] || [];
    // Validate that all metrics exist in allColDefs
    const validMetricFields = allColDefs.map((col) => col.field);
    const validMetrics = metrics.filter((metric) => validMetricFields.includes(metric));
    setSelectedMetrics(validMetrics);
    setActivePreset(name);
  };

  // Delete a preset
  const handleDeletePreset = async (name) => {
    try {
      const token = await getAccessTokenSilently({ audience: "https://api.softwareiq.io" });
      await dispatch(deleteMetricPreset({ token, presetName: name })).unwrap();
      trackEvent('preset_change', { preset_type: 'metric', operation: 'delete' });
      if (name === activePreset) {
        setActivePreset(null);
      }
    } catch (err) {
      console.error("Error deleting metric preset:", err);
    }
  };

  // Signup handlers
  const handleSignupClick = () => {
    localStorage.setItem('redirectTo', window.location.pathname);
    localStorage.setItem("requestedTier", "free");
    loginWithRedirect({
      screen_hint: "signup",
      appState: { requestedTier: "free" },
      authorizationParams: { screen_hint: "signup", scope: "openid email profile" }
    });
  };

  const handleLoginClick = () => {
    localStorage.setItem('redirectTo', window.location.pathname);
    loginWithRedirect();
  };

  const selectBorderColor =
    theme.palette.mode === "light"
      ? alpha(theme.palette.common.black, 0.23)
      : alpha(theme.palette.common.white, 0.23);

  const hasPresets = Object.keys(metricPresets).length > 0;

  return (
    <Box>
      {/* Preset icon button */}
      <Tooltip title="Metric Presets">
        <IconButton
          ref={inputRef}
          onClick={handleToggle}
          size="small"
          sx={{
            backgroundColor: theme.tableHeader.highlight,
            height: "30px",
            width: "30px",
            color: activePreset
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
            ...(leftBorderOnly
              ? {
                  border: 'none',
                  borderLeft: `1px solid ${selectBorderColor}`,
                  borderRadius: 0,
                }
              : {
                  border: `1px solid ${selectBorderColor}`,
                  borderRadius: 1,
                }
            ),
          }}
        >
          {activePreset ? (
            <BookmarkIcon fontSize="small" />
          ) : (
            <BookmarkBorderIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>

      {/* Signup Dialog for unauthenticated users */}
      <Dialog
        open={showSignupDialog}
        onClose={() => setShowSignupDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            p: 1.5,
            borderRadius: 3,
            boxShadow: theme.shadows[8],
            backdropFilter: "blur(6px)",
            backgroundColor: theme.background.transparent,
          }
        }}
      >
        <Box sx={{ position: "absolute", top: 8, right: 8 }}>
          <IconButton onClick={() => setShowSignupDialog(false)} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogTitle sx={{ fontWeight: 700, pr: 5 }}>
          Save Your Metric Presets
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a free account to save and manage your metric presets.
          </Typography>

          <Box sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: theme.palette.mode === "dark" ? "rgba(80,100,255,0.12)" : "rgba(80,100,255,0.10)",
            border: `1px dashed ${theme.palette.primary.main}`,
            fontSize: 12,
          }}>
            Sign up today to save custom metric selections, quickly switch between views, and access your presets across all pages.
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleLoginClick} variant="text">Login</Button>
          <Button onClick={handleSignupClick} variant="contained">Create Free Account</Button>
        </DialogActions>
      </Dialog>

      {/* Popper menu - only shown when authenticated */}
      <Popper open={open} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper
            sx={{
              p: 2,
              mt: 1,
              width: 280,
              maxWidth: 280,
              maxHeight: 400,
              overflowY: "auto",
              backgroundColor: theme.background.transparent,
              backdropFilter: "blur(6px)",
              color: theme.palette.text.secondary,
            }}
          >
            {/* Presets heading */}
            <Typography variant="subtitle2" fontSize={13} mb={1}>
              Metric Presets
            </Typography>

            {/* List of saved presets */}
            {!hasPresets ? (
              <Typography fontSize={11} color="text.secondary" mb={1}>
                No saved presets
              </Typography>
            ) : (
              Object.keys(metricPresets).map((name) => {
                const changed = activePreset === name && isPresetChanged(name);
                const isActive = name === activePreset;
                return (
                  <Box
                    key={name}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 0.5,
                      backgroundColor: isActive
                        ? theme.tableHeader.highlightDark
                        : "transparent",
                      borderRadius: 1,
                      px: 0.5,
                      py: 0.25,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexGrow: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        fontSize={13}
                        sx={{
                          cursor: "pointer",
                          color: theme.palette.primary.main,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          ...(isActive
                            ? { fontWeight: "bold", textDecoration: "underline" }
                            : {}),
                        }}
                        onClick={() => loadPreset(name)}
                      >
                        {name}
                      </Typography>

                      {changed && (
                        <Tooltip title="Save changes">
                          <IconButton
                            onClick={() => saveExistingPreset(name)}
                            size="small"
                            sx={{ ml: 0.5 }}
                          >
                            <SaveIcon
                              fontSize="small"
                              sx={{ color: theme.palette.success.main, fontSize: "18px" }}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    <Tooltip title="Delete preset">
                      <IconButton
                        onClick={() => handleDeletePreset(name)}
                        size="small"
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon
                          fontSize="small"
                          sx={{ color: theme.palette.text.secondary, fontSize: "18px" }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })
            )}

            {/* Add new preset */}
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <InputBase
                inputRef={newPresetInputRef}
                placeholder="New preset name"
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    saveNewPreset();
                  }
                }}
                sx={{
                  backgroundColor: theme.tableHeader.highlight,
                  fontSize: 12,
                  px: 1,
                  py: 0.5,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  flexGrow: 1,
                }}
              />
              <Tooltip title="Save current metrics as preset">
                <IconButton
                  onClick={saveNewPreset}
                  size="small"
                  disabled={!newPresetName.trim() || selectedMetrics.length === 0}
                  sx={{ color: theme.palette.success.main }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Metric count info */}
            <Typography fontSize={10} color="text.secondary" mt={1}>
              Current selection: {selectedMetrics.length} metric{selectedMetrics.length !== 1 ? 's' : ''}
            </Typography>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}

export default MetricPresets;
